import React, { forwardRef, Ref, useImperativeHandle, useState, useEffect } from "react";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import { useStoreDispatch, useStoreSelector } from "../../../../../../../hooks/StoreHooks";
import { ModuleType, InstallationType, LatoInstallation } from "./Pergola_type";
import { usePergolaConfigurationBuilderActions } from "../../../../../../../store/reducers/pergola_configuration_builder";
import { DimensionPergola, Pilastri } from "../../../../../../models/pergola_configuration";

export type FrmImpostazioniInizialiHandle = {
  checkFields: () => void;
  getModulo: () => ModuleType;
  getFormData: () => DimensionPergola;
  getPilastriData: () => Pilastri[];
  isPatioMultiModulo: () => boolean;
};

const MIN_LARGHEZZA = 2000;
const MIN_PROFONDITA = 2000;
const MIN_ALTEZZA_PILASTRO = 2000;
const MAX_ALTEZZA_PILASTRO = 30000;
const MIN_QUANTITA = 1;
const MIN_ALTEZZA_MURO = 10;
const MAX_ALTEZZA_MURO = 3000;

type DirezioneMuro = "Fronte" | "Retro" | "Sinistra" | "Destra";
type PilastroDirection = "H1" | "H2" | "H3" | "H4";

export const FrmImpostazioniIniziali = forwardRef<FrmImpostazioniInizialiHandle, any>(
  (props, ref: Ref<FrmImpostazioniInizialiHandle>) => {
    const { pergolaConfigurationBuilder } = useStoreSelector((store) => store);
    const dispatcher = useStoreDispatch();
    const {
      setQuantita,
      setLarghezza,
      setProfondita,
      setAltezza,
      setPergolaDimension,
      setMuroSingolo,
      setPilastroSingolo,
      removePergolaSide,
      setStepVisible,
    } = usePergolaConfigurationBuilderActions();

    const [modulo] = useState<ModuleType>(ModuleType.modulo_singolo);
    
    // Input
    const [quantitaInput, setQuantitaInput] = useState(String(pergolaConfigurationBuilder.configuration?.dimensioni.quantita ?? 1));
    const [larghezzaInput, setLarghezzaInput] = useState(String(pergolaConfigurationBuilder.configuration?.dimensioni.larghezza ?? 4000));
    const [profonditaInput, setProfonditaInput] = useState(String(pergolaConfigurationBuilder.configuration?.dimensioni.profondita ?? 3000));

    const [pilastroH1, setPilastroH1] = useState<string>(
      String(pergolaConfigurationBuilder.pilastri.find(p => p.direzione === "H1")?.altezza ?? 2000)
    );
    const [pilastroH2, setPilastroH2] = useState<string>(
      String(pergolaConfigurationBuilder.pilastri.find(p => p.direzione === "H2")?.altezza ?? 2000)
    );
    const [pilastroH3, setPilastroH3] = useState<string>(
      String(pergolaConfigurationBuilder.pilastri.find(p => p.direzione === "H3")?.altezza ?? 2000)
    );
    const [pilastroH4, setPilastroH4] = useState<string>(
      String(pergolaConfigurationBuilder.pilastri.find(p => p.direzione === "H4")?.altezza ?? 2000)
    );

    const [muroFronte, setMuroFronte] = useState<string>(
      String(pergolaConfigurationBuilder.muri.find(p => p.direzione === "Fronte")?.altezza ?? 0)
    );
    const [muroSinistra, setMuroSinistra] = useState<string>(
      String(pergolaConfigurationBuilder.muri.find(p => p.direzione === "Sinistra")?.altezza ?? 0)
    );
    const [muroRetro, setMuroRetro] = useState<string>(
      String(pergolaConfigurationBuilder.muri.find(p => p.direzione === "Retro")?.altezza ?? 0)
    );
    const [muroDestra, setMuroDestra] = useState<string>(
      String(pergolaConfigurationBuilder.muri.find(p => p.direzione === "Destra")?.altezza ?? 0)
    );

    // Errori
    const [errQta, setErrQta] = useState<string | null>(null);
    const [errLarghezza, setErrLarghezza] = useState<string | null>(null);
    const [errProfondita, setErrProfondita] = useState<string | null>(null);
    const [errH1, setErrH1] = useState<string | null>(null);
    const [errH2, setErrH2] = useState<string | null>(null);
    const [errH3, setErrH3] = useState<string | null>(null);
    const [errH4, setErrH4] = useState<string | null>(null);
    const [errMuroF, setErrMuroF] = useState<string | null>(null);
    const [errMuroS, setErrMuroS] = useState<string | null>(null);
    const [errMuroR, setErrMuroR] = useState<string | null>(null);
    const [errMuroD, setErrMuroD] = useState<string | null>(null);

    const formik = useFormik<DimensionPergola>({
      initialValues: {
        quantita: pergolaConfigurationBuilder.configuration?.dimensioni.quantita ?? 1,
        larghezza: pergolaConfigurationBuilder.configuration?.dimensioni.larghezza ?? 4000,
        profondita: pergolaConfigurationBuilder.configuration?.dimensioni.profondita ?? 3000,
        installazione: pergolaConfigurationBuilder.configuration?.dimensioni.installazione ?? InstallationType.nuove_conf,
        altezza: 2500,
        lati: (() => {
        const saved = pergolaConfigurationBuilder.configuration?.dimensioni.lati ?? [];
        const dirs: Array<"Fronte" | "Sinistra" | "Retro" | "Destra"> = ["Fronte", "Sinistra", "Retro", "Destra"];
        return dirs.map(d => saved.find(l => l.direzione === d) ?? { direzione: d, tipo: LatoInstallation.libero });
      })()
      },
      onSubmit: () => {},
    });
    useEffect(() => {
      dispatcher(setPergolaDimension(formik.values));
    }, [formik.values]);
    useEffect(() => {
      const TOTAL_LATI = 4;
      const tuttiAddossati =
        (formik.values.lati?.length ?? 0) === TOTAL_LATI &&
        formik.values.lati!.every(l => l.tipo === LatoInstallation.addossato);

      dispatcher(setStepVisible({ stepSlug: "chiusure", isVisible: !tuttiAddossati }));
      dispatcher(setStepVisible({ stepSlug: "estetica", isVisible: !tuttiAddossati }));
    }, [formik.values.lati]);

    useEffect(() => {
      const h1 = Number(pilastroH1) || 0;
      const h2 = Number(pilastroH2) || 0;
      const h3 = Number(pilastroH3) || 0;
      const h4 = Number(pilastroH4) || 0;
      let maxH = Math.max(h1, h2, h3, h4);

      if (maxH >= MIN_ALTEZZA_PILASTRO) {
        formik.setFieldValue("altezza", maxH);
        dispatcher(setAltezza(maxH));
      }
    }, [pilastroH1, pilastroH2, pilastroH3, pilastroH4, dispatcher]);

    const isDigitsOnly = (s: string) => /^$|^\d+$/.test(s);

    const commitDimension = (
      raw: string,
      min: number,
      setter: (v: string) => void,
      setError: (e: string | null) => void,
      field: "quantita" | "larghezza" | "profondita",
      action: (v: number) => any
    ) => {
      let num = Number(raw);
      if (isNaN(num) || raw.trim() === "" || num < min) {
        num = min;
        setError(`Minimo ${min}${field !== "quantita" ? " mm" : ""}`);
      } else {
        setError(null);
      }
      setter(String(num));
      formik.setFieldValue(field, num);
      dispatcher(action(num));
    };

    const commitPilastro = (
      raw: string,
      direzione: PilastroDirection,
      setter: (v: string) => void,
      setError: (e: string | null) => void
    ) => {
      let num = Number(raw);
      if (isNaN(num) || raw.trim() === "" || num < MIN_ALTEZZA_PILASTRO) {
        setError(`Minimo ${MIN_ALTEZZA_PILASTRO} mm`);
        num = MIN_ALTEZZA_PILASTRO;
      } else if (num > MAX_ALTEZZA_PILASTRO) {
        setError(`Massimo ${MAX_ALTEZZA_PILASTRO} mm`);
        num = MAX_ALTEZZA_PILASTRO;
      } else {
        setError(null);
      }
      setter(String(num));
      dispatcher(setPilastroSingolo({ direzione, altezza: num }));
    };

    const commitMuro = (
      raw: string,
      direzione: DirezioneMuro,
      setter: (v: string) => void,
      setError: (e: string | null) => void
    ) => {
      let num = Number(raw);
      if (isNaN(num) || raw.trim() === "" || num <= 0) {
        num = 0;
        setError(null);
      } else if (num < MIN_ALTEZZA_MURO) {
        setError(`Min ${MIN_ALTEZZA_MURO} mm`);
        num = MIN_ALTEZZA_MURO;
      } else if (num > MAX_ALTEZZA_MURO) {
        setError(`Max ${MAX_ALTEZZA_MURO} mm`);
        num = MAX_ALTEZZA_MURO;
      } else {
        setError(null);
      }
      setter(String(num));
      dispatcher(setMuroSingolo({ direzione, altezza: num }));
    };
    useImperativeHandle(ref, () => ({
      checkFields: () => {
        commitDimension(quantitaInput, MIN_QUANTITA, setQuantitaInput, setErrQta, "quantita", setQuantita);
        commitDimension(larghezzaInput, MIN_LARGHEZZA, setLarghezzaInput, setErrLarghezza, "larghezza", setLarghezza);
        commitDimension(profonditaInput, MIN_PROFONDITA, setProfonditaInput, setErrProfondita, "profondita", setProfondita);

        commitPilastro(pilastroH1, "H1", setPilastroH1, setErrH1);
        commitPilastro(pilastroH2, "H2", setPilastroH2, setErrH2);
        commitPilastro(pilastroH3, "H3", setPilastroH3, setErrH3);
        commitPilastro(pilastroH4, "H4", setPilastroH4, setErrH4);

        commitMuro(muroFronte, "Fronte", setMuroFronte, setErrMuroF);
        commitMuro(muroSinistra, "Sinistra", setMuroSinistra, setErrMuroS);
        commitMuro(muroRetro, "Retro", setMuroRetro, setErrMuroR);
        commitMuro(muroDestra, "Destra", setMuroDestra, setErrMuroD);
      },
      getModulo: () => modulo,
      getFormData: () => formik.values,
      getPilastriData: () => {
        const pairs: { val: string; dir: PilastroDirection }[] = [
          { val: pilastroH1, dir: "H1" },
          { val: pilastroH2, dir: "H2" },
          { val: pilastroH3, dir: "H3" },
          { val: pilastroH4, dir: "H4" },
        ];
        return pairs
          .map(({ val, dir }) => ({
            direzione: dir,
            altezza: Math.min(MAX_ALTEZZA_PILASTRO, Math.max(MIN_ALTEZZA_PILASTRO, Number(val) || MIN_ALTEZZA_PILASTRO)),
          }))
          .filter(p => p.altezza > 0);
      },
      isPatioMultiModulo: () => {
        const larghezza = formik.values.larghezza ?? 0;
        const profondita = formik.values.profondita ?? 0;
        const lati = formik.values.lati ?? [];

        const piuModuli = larghezza > 4100 || profondita > 7000;

        const countAddossati = lati.filter(l => l.tipo === LatoInstallation.addossato).length;
        const fronte = lati.find(l => l.direzione === "Fronte")?.tipo;
        const retro = lati.find(l => l.direzione === "Retro")?.tipo;
        const sinistra = lati.find(l => l.direzione === "Sinistra")?.tipo;
        const destra = lati.find(l => l.direzione === "Destra")?.tipo;

        const latiOppostiAddossati =
          (fronte === LatoInstallation.addossato && retro === LatoInstallation.addossato) ||
          (sinistra === LatoInstallation.addossato && destra === LatoInstallation.addossato);

        const isPatio = countAddossati >= 3 || latiOppostiAddossati;

        return piuModuli && isPatio;
      },
    }));

    return (
      <div>
        <TextField
          variant="outlined"
          label="Quantità"
          type="number"
          className="mt-3 w-full"
          value={quantitaInput}
          onFocus={() => setErrQta(null)}
          onChange={(e) => isDigitsOnly(e.target.value) && setQuantitaInput(e.target.value)}
          onBlur={() => commitDimension(quantitaInput, MIN_QUANTITA, setQuantitaInput, setErrQta, "quantita", setQuantita)}
          error={!!errQta}
          helperText={errQta}
        />
        {/*<FormControl fullWidth className="mt-3">
          <InputLabel id="tipoPergolaLabel">Tipo pergola</InputLabel>
          <Select
            labelId="tipoPergolaLabel"
            value={formik.values.installazione}
            label="Tipo pergola"
            onChange={(e) => {
              const val = e.target.value as InstallationType;
              formik.setFieldValue("installazione", val);
              dispatcher(setPergolaDimension({ ...formik.values, installazione: val }));

              if (val === InstallationType.patio) {
                dispatcher(removePergolaSide({ direzione: "Retro" }));
                dispatcher(removePergolaSide({ direzione: "Fronte" }));
              }
              if (val === InstallationType.addossata_profondita) {
                dispatcher(removePergolaSide({ direzione: "Sinistra" }));
              }
              if (val === InstallationType.addossata_larghezza) {
                dispatcher(removePergolaSide({ direzione: "Retro" }));
              }
              if (val === InstallationType.angolare) {
                dispatcher(removePergolaSide({ direzione: "Retro" }));
                dispatcher(removePergolaSide({ direzione: "Sinistra" }));
              }
            }}
          >
            <MenuItem value={InstallationType.installazione_libera}>INSTALLAZIONE LIBERA</MenuItem>
            <MenuItem value={InstallationType.addossata_profondita}>ADDOSSATA in PROFONDITÀ</MenuItem>
            <MenuItem value={InstallationType.addossata_larghezza}>ADDOSSATA in LARGHEZZA</MenuItem>
            <MenuItem value={InstallationType.angolare}>ADDOSSATA in LARGHEZZA e PROFONDITÀ </MenuItem>
            <MenuItem value={InstallationType.patio}>PATIO</MenuItem>
          </Select>
        </FormControl>*/}
        
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
          <TextField
            variant="outlined"
            label="Larghezza Pergola"
            type="number"
            value={larghezzaInput}
            onFocus={() => setErrLarghezza(null)}
            onChange={(e) => isDigitsOnly(e.target.value) && setLarghezzaInput(e.target.value)}
            onBlur={() => commitDimension(larghezzaInput, MIN_LARGHEZZA, setLarghezzaInput, setErrLarghezza, "larghezza", setLarghezza)}
            InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
            error={!!errLarghezza}
            helperText={errLarghezza}
          />

          <TextField
            variant="outlined"
            label="Profondità Pergola"
            type="number"
            value={profonditaInput}
            onFocus={() => setErrProfondita(null)}
            onChange={(e) => isDigitsOnly(e.target.value) && setProfonditaInput(e.target.value)}
            onBlur={() => commitDimension(profonditaInput, MIN_PROFONDITA, setProfonditaInput, setErrProfondita, "profondita", setProfondita)}
            InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
            error={!!errProfondita}
            helperText={errProfondita}
          />
        </div>

        {/* PILASTRI */}
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-3">Altezze Pergola</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TextField variant="outlined" label="H1" type="number" value={pilastroH1} onFocus={() => setErrH1(null)} onChange={(e) => isDigitsOnly(e.target.value) && setPilastroH1(e.target.value)} onBlur={() => commitPilastro(pilastroH1, "H1", setPilastroH1, setErrH1)} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} error={!!errH1} helperText={errH1} />
            <TextField variant="outlined" label="H2" type="number" value={pilastroH2} onFocus={() => setErrH2(null)} onChange={(e) => isDigitsOnly(e.target.value) && setPilastroH2(e.target.value)} onBlur={() => commitPilastro(pilastroH2, "H2", setPilastroH2, setErrH2)} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} error={!!errH2} helperText={errH2} />
            <TextField variant="outlined" label="H3" type="number" value={pilastroH3} onFocus={() => setErrH3(null)} onChange={(e) => isDigitsOnly(e.target.value) && setPilastroH3(e.target.value)} onBlur={() => commitPilastro(pilastroH3, "H3", setPilastroH3, setErrH3)} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} error={!!errH3} helperText={errH3} />
            <TextField variant="outlined" label="H4" type="number" value={pilastroH4} onFocus={() => setErrH4(null)} onChange={(e) => isDigitsOnly(e.target.value) && setPilastroH4(e.target.value)} onBlur={() => commitPilastro(pilastroH4, "H4", setPilastroH4, setErrH4)} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} error={!!errH4} helperText={errH4} />
          </div>
        </div>
        {/* LATI */}
        <div className="mt-2">
          <h3 className="text-lg font-medium mb-3">Tipologia Lato</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <FormControl fullWidth>
                <InputLabel>Lato A (Fronte)</InputLabel>
                <Select
                  label="Lato A (Fronte)"
                  value={
                    formik.values.lati?.find(l => l.direzione === "Fronte")?.tipo ??
                    LatoInstallation.libero
                  }
                  onChange={(e) => {
                    const val = e.target.value as LatoInstallation;
                    formik.setFieldValue("lati", [
                      ...(formik.values.lati || []).filter(l => l.direzione !== "Fronte"),
                      { direzione: "Fronte", tipo: val }
                    ]);
                    dispatcher(removePergolaSide({ direzione: "Fronte" }));
                    if (val !== LatoInstallation.con_muretto) {
                      setMuroFronte("0");
                      dispatcher(setMuroSingolo({ direzione: "Fronte", altezza: 0 }));
                    }
                  }}
                >
                  <MenuItem value={LatoInstallation.libero}>LIBERO</MenuItem>
                  <MenuItem value={LatoInstallation.addossato}>ADDOSSATO</MenuItem>
                  <MenuItem value={LatoInstallation.con_muretto}>CON MURETTO</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Lato B (Sinistra)</InputLabel>
                <Select
                  label="Lato B (Sinistra)"
                  value={
                    formik.values.lati?.find(l => l.direzione === "Sinistra")?.tipo ??
                    LatoInstallation.libero
                  }
                  onChange={(e) => {
                    const val = e.target.value as LatoInstallation;
                    formik.setFieldValue("lati", [
                      ...(formik.values.lati || []).filter(l => l.direzione !== "Sinistra"),
                      { direzione: "Sinistra", tipo: val }
                    ]);
                    dispatcher(removePergolaSide({ direzione: "Sinistra" }));
                    if (val !== LatoInstallation.con_muretto) {
                      setMuroSinistra("0");
                      dispatcher(setMuroSingolo({ direzione: "Sinistra", altezza: 0 }));
                    }
                  }}
                >
                  <MenuItem value={LatoInstallation.libero}>LIBERO</MenuItem>
                  <MenuItem value={LatoInstallation.addossato}>ADDOSSATO</MenuItem>
                  <MenuItem value={LatoInstallation.con_muretto}>CON MURETTO</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Lato C (Retro)</InputLabel>
                <Select
                  label="Lato C (Retro)"
                  value={
                    formik.values.lati?.find(l => l.direzione === "Retro")?.tipo ??
                    LatoInstallation.libero
                  }
                  onChange={(e) => {
                    const val = e.target.value as LatoInstallation;
                    formik.setFieldValue("lati", [
                      ...(formik.values.lati || []).filter(l => l.direzione !== "Retro"),
                      { direzione: "Retro", tipo: val }
                    ]);
                    dispatcher(removePergolaSide({ direzione: "Retro" }));
                    if (val !== LatoInstallation.con_muretto) {
                      setMuroRetro("0");
                      dispatcher(setMuroSingolo({ direzione: "Retro", altezza: 0 }));
                    }
                  }}
                >
                  <MenuItem value={LatoInstallation.libero}>LIBERO</MenuItem>
                  <MenuItem value={LatoInstallation.addossato}>ADDOSSATO</MenuItem>
                  <MenuItem value={LatoInstallation.con_muretto}>CON MURETTO</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Lato D (Destra)</InputLabel>
                <Select
                  label="Lato D (Destra)"
                  value={
                    formik.values.lati?.find(l => l.direzione === "Destra")?.tipo ??
                    LatoInstallation.libero
                  }
                  onChange={(e) => {
                    const val = e.target.value as LatoInstallation;
                    formik.setFieldValue("lati", [
                      ...(formik.values.lati || []).filter(l => l.direzione !== "Destra"),
                      { direzione: "Destra", tipo: val }
                    ]);
                    dispatcher(removePergolaSide({ direzione: "Destra" }));
                    if (val !== LatoInstallation.con_muretto) {
                      setMuroDestra("0");
                      dispatcher(setMuroSingolo({ direzione: "Destra", altezza: 0 }));
                    }
                  }}
                >
                  <MenuItem value={LatoInstallation.libero}>LIBERO</MenuItem>
                  <MenuItem value={LatoInstallation.addossato}>ADDOSSATO</MenuItem>
                  <MenuItem value={LatoInstallation.con_muretto}>CON MURETTO</MenuItem>
                </Select>
              </FormControl>

          </div>
        </div>
        {/* MURI - visibili solo se il lato corrispondente è CON MURETTO */}
        {(formik.values.lati?.some(l => l.tipo === LatoInstallation.con_muretto)) && (
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-3">Altezze Muri</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {formik.values.lati?.find(l => l.direzione === "Fronte")?.tipo === LatoInstallation.con_muretto && (
                <TextField variant="outlined" label="Muro A (Fronte)" type="number" value={muroFronte} onFocus={() => setErrMuroF(null)} onChange={(e) => isDigitsOnly(e.target.value) && setMuroFronte(e.target.value)} onBlur={() => commitMuro(muroFronte, "Fronte", setMuroFronte, setErrMuroF)} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} error={!!errMuroF} helperText={errMuroF} />
              )}
              {formik.values.lati?.find(l => l.direzione === "Sinistra")?.tipo === LatoInstallation.con_muretto && (
                <TextField variant="outlined" label="Muro B (Sinistra)" type="number" value={muroSinistra} onFocus={() => setErrMuroS(null)} onChange={(e) => isDigitsOnly(e.target.value) && setMuroSinistra(e.target.value)} onBlur={() => commitMuro(muroSinistra, "Sinistra", setMuroSinistra, setErrMuroS)} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} error={!!errMuroS} helperText={errMuroS} />
              )}
              {formik.values.lati?.find(l => l.direzione === "Retro")?.tipo === LatoInstallation.con_muretto && (
                <TextField variant="outlined" label="Muro C (Retro)" type="number" value={muroRetro} onFocus={() => setErrMuroR(null)} onChange={(e) => isDigitsOnly(e.target.value) && setMuroRetro(e.target.value)} onBlur={() => commitMuro(muroRetro, "Retro", setMuroRetro, setErrMuroR)} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} error={!!errMuroR} helperText={errMuroR} />
              )}
              {formik.values.lati?.find(l => l.direzione === "Destra")?.tipo === LatoInstallation.con_muretto && (
                <TextField variant="outlined" label="Muro D (Destra)" type="number" value={muroDestra} onFocus={() => setErrMuroD(null)} onChange={(e) => isDigitsOnly(e.target.value) && setMuroDestra(e.target.value)} onBlur={() => commitMuro(muroDestra, "Destra", setMuroDestra, setErrMuroD)} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} error={!!errMuroD} helperText={errMuroD} />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

FrmImpostazioniIniziali.displayName = "FrmImpostazioniIniziali";